/**
 * Bootstraps the session: calls /api/v1/me on mount, hydrates the zustand
 * store, and gates protected routes behind a redirect to /login.
 *
 * In development the backend accepts dev headers — /me will succeed even
 * without an actual login flow, so the AuthGate is mostly a transparent
 * pass-through unless the cookie/token expires.
 */
import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Auth } from '@/lib/api/v1';
import { useSession, usePrefs } from '@/lib/store';
import { Spinner } from '@/design-system';

export function AuthBootstrap({ children }: { children: ReactNode }) {
  const { user, hydrated, setSession, setHydrated } = useSession();
  const { activeTenantId, setActiveTenantId } = usePrefs();

  useEffect(() => {
    let mounted = true;
    Auth.me()
      .then((data) => {
        if (!mounted) return;
        setSession({ user: data.user, memberships: data.memberships, role: data.role ?? null });
        if (!activeTenantId && data.defaultTenantId) setActiveTenantId(data.defaultTenantId);
      })
      .catch(() => {
        if (!mounted) return;
        // 401 / 403 -> clear persisted dev/session state; AuthGate handles the redirect.
        setSession({ user: null, memberships: [], role: null });
        setActiveTenantId(null);
      })
      .finally(() => {
        if (mounted) setHydrated(true);
      });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hydrated && !user) {
    return (
      <div className="grid min-h-screen place-items-center">
        <Spinner size={28} />
      </div>
    );
  }

  return <>{children}</>;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, hydrated } = useSession();
  const location = useLocation();

  if (!hydrated) {
    return (
      <div className="grid min-h-screen place-items-center">
        <Spinner size={28} />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return <>{children}</>;
}
